/**
 * Attendance Exemption Staff Register (web only, authorized HR).
 * SRS: HR filters by Branch and Designation, ticks employees (or Select All), then sets a Date Range
 * or ticks Unlimited. Exempt staff get no pending absences. No file upload.
 * Only the employee's HRM ID and the dates are stored; the filters are just a way to find people.
 * The whole active-employee list is loaded once; Region -> Branch, Department -> Designation and a
 * name / ID search narrow it on the page. The selection survives filter changes, and Save asks HR
 * to confirm the selected people first.
 */
import React, { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import * as XLSX from "xlsx";
import { ToastContainer, toast } from "react-toastify";
import { pendingAbsenceApi } from "../../utils/pendingAbsenceApi";
import LoadingIndicator from "../Common/components/LoadingIndicator";
import {
  ActionDialog,
  DateInput,
  NoPermission,
  PageHeading,
  usePagePermission,
} from "./PendingAbsenceCommon";
import {
  buildCascadeOptions,
  buildExemptionRows,
  formatDate,
  reconcileFilters,
  uniqueEmployees,
} from "./pendingAbsenceHelpers";

const PERMISSION_TITLE = "Attendance Exemption";
const ALL = { value: 0, label: "All" };
const EMPTY_FILTERS = {
  region: ALL,
  branch: ALL,
  department: ALL,
  designation: ALL,
  employee: ALL, // not shown here; the helpers expect the key
};

const STATE_LABELS = { ACTIVE: "Active", UPCOMING: "Starts later", ENDED: "Ended" };

function describeExemption(exemption) {
  return exemption.is_unlimited
    ? `${formatDate(exemption.start_date)} - Unlimited`
    : `${formatDate(exemption.start_date)} - ${formatDate(exemption.end_date)}`;
}

export default function AttendanceExemption() {
  const permission = usePagePermission(PERMISSION_TITLE);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [search, setSearch] = useState("");
  const [hideExempt, setHideExempt] = useState(false);

  const [employees, setEmployees] = useState(null); // null = not loaded yet
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirming, setConfirming] = useState(false);

  const [range, setRange] = useState({ start: "", end: "", unlimited: false });
  const [saving, setSaving] = useState(false);

  const [registered, setRegistered] = useState([]);
  const [loadingRegistered, setLoadingRegistered] = useState(true);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadRegistered = useCallback(async () => {
    setLoadingRegistered(true);
    try {
      setRegistered((await pendingAbsenceApi.get("/exemptions")) || []);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingRegistered(false);
    }
  }, []);

  useEffect(() => {
    if (permission.loaded && permission.isView) loadRegistered();
  }, [permission.loaded, permission.isView, loadRegistered]);

  // The whole active-employee list, once. Filtering is done on the page.
  const loadEmployees = useCallback(async () => {
    setLoadingEmployees(true);
    try {
      setEmployees(uniqueEmployees(await pendingAbsenceApi.get("/exemptions/employees")));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingEmployees(false);
    }
  }, []);

  useEffect(() => {
    if (permission.loaded && permission.isView && permission.isAddNew) loadEmployees();
  }, [permission.loaded, permission.isView, permission.isAddNew, loadEmployees]);

  const toggleEmployee = (userId) => {
    const next = new Set(selected);
    if (next.has(userId)) next.delete(userId);
    else next.add(userId);
    setSelected(next);
  };

  const options = buildCascadeOptions(employees || [], filters);
  const setFilter = (key) => (value) =>
    setFilters(reconcileFilters({ ...filters, [key]: value }, employees || []));
  const hasFilter =
    search.trim() !== "" ||
    hideExempt ||
    ["region", "branch", "department", "designation"].some((key) => filters[key].value);

  const matches = (employee) => {
    const id = (key) => filters[key].value;
    if (id("region") && employee.region_id !== id("region")) return false;
    if (id("branch") && employee.branch_id !== id("branch")) return false;
    if (id("department") && employee.department_id !== id("department")) return false;
    if (id("designation") && employee.designation_id !== id("designation")) return false;
    if (hideExempt && employee.exemptions.length > 0) return false;
    const term = search.trim().toLowerCase();
    return term === "" || String(employee.display_name).toLowerCase().includes(term);
  };
  const shown = (employees || []).filter(matches);

  // Select all / clear apply to the rows shown; selections made under other filters are kept.
  const allSelected = shown.length > 0 && shown.every((e) => selected.has(e.user_id));
  const toggleAllEmployees = () => {
    const next = new Set(selected);
    shown.forEach((e) => (allSelected ? next.delete(e.user_id) : next.add(e.user_id)));
    setSelected(next);
  };
  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearch("");
    setHideExempt(false);
  };
  const selectedEmployees = (employees || []).filter((e) => selected.has(e.user_id));

  const rangeError = () => {
    if (selected.size === 0) return "Select at least one employee.";
    if (!range.start) return "Choose the start date.";
    if (!range.unlimited) {
      if (!range.end) return "Choose the end date, or tick Unlimited.";
      if (range.end < range.start) return "The end date cannot be earlier than the start date.";
    }
    return null;
  };

  const askToConfirm = () => {
    const error = rangeError();
    if (error) return toast.error(error);
    setConfirming(true);
  };

  const save = async () => {
    const error = rangeError();
    if (error) return toast.error(error);

    setSaving(true);
    try {
      const result = await pendingAbsenceApi.post("/exemptions", {
        user_ids: [...selected],
        start_date: range.start,
        end_date: range.unlimited ? null : range.end,
        is_unlimited: range.unlimited,
      });
      if (result.created > 0) {
        toast.success(`${result.created} employee${result.created === 1 ? "" : "s"} registered.`);
      }
      const names = new Map((employees || []).map((e) => [e.user_id, e.display_name]));
      (result.skipped || []).forEach((skip) =>
        toast.warn(`${names.get(skip.user_id) || skip.user_id}: ${skip.reason}`, { autoClose: 9000 })
      );
      setConfirming(false);
      setSelected(new Set());
      await Promise.all([loadRegistered(), loadEmployees()]);
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  // Excel of the exemptions that are active today (not upcoming or ended ones).
  const activeRows = buildExemptionRows(registered);
  const exportActive = () => {
    if (activeRows.length === 0) return toast.info("There are no active exemptions to export.");
    const sheet = XLSX.utils.json_to_sheet(activeRows);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "Active Exemptions");
    XLSX.writeFile(book, `attendance_exemptions_active_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await pendingAbsenceApi.delete(`/exemptions/${toDelete.exemption_id}`);
      toast.success("Exemption deleted.");
      setToDelete(null);
      await loadRegistered();
      if (employees) await loadEmployees();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeleting(false);
    }
  };

  if (!permission.loaded) return <LoadingIndicator marginTop={35} />;
  if (!permission.isView) return <NoPermission />;

  return (
    <div>
      <ToastContainer position={toast.POSITION.TOP_RIGHT} />
      <PageHeading title="Attendance Exemption" />
      <div className="wrapper wrapper-content animated fadeInRight pending-absence-page">
        {permission.isAddNew ? (
          <div className="ibox float-e-margins">
            <div className="ibox-title">
              <h5>Register staff who are exempt from attendance tracking</h5>
            </div>
            <div className="ibox-content">
              <div className="pa-form-section">
                <div className="row">
                  <div className="col-sm-3 form-group">
                    <label>Region</label>
                    <Select value={filters.region} options={options.region} onChange={setFilter("region")} />
                  </div>
                  <div className="col-sm-3 form-group">
                    <label>Branch</label>
                    <Select value={filters.branch} options={options.branch} onChange={setFilter("branch")} />
                  </div>
                  <div className="col-sm-3 form-group">
                    <label>Department</label>
                    <Select value={filters.department} options={options.department} onChange={setFilter("department")} />
                  </div>
                  <div className="col-sm-3 form-group">
                    <label>Designation</label>
                    <Select value={filters.designation} options={options.designation} onChange={setFilter("designation")} />
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-4 form-group">
                    <label>Search by name or ID</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Aye Aye or A-00004"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                    />
                  </div>
                  <div className="col-sm-4 form-group pa-filter-option">
                    <label style={{ fontWeight: 400 }}>
                      <input
                        type="checkbox"
                        aria-label="Hide employees who already have an exemption"
                        checked={hideExempt}
                        onChange={(event) => setHideExempt(event.target.checked)}
                      />{" "}
                      Hide employees who already have an exemption
                    </label>
                  </div>
                  <div className="col-sm-4 form-group pa-filter-option">
                    <button type="button" className="btn btn-white" disabled={!hasFilter} onClick={clearFilters}>
                      Clear filters
                    </button>
                  </div>
                </div>

                {loadingEmployees && employees === null && <LoadingIndicator backgroundColor="#ffffff" />}

                {employees && (
                  <p className="text-muted" data-testid="exemption-counts">
                    {shown.length} employee{shown.length === 1 ? "" : "s"} shown, {selected.size} selected
                    {selected.size > 0 && (
                      <>
                        {" "}
                        (<a href="#clear" onClick={(event) => { event.preventDefault(); setSelected(new Set()); }}>Clear selection</a>)
                      </>
                    )}
                  </p>
                )}

                {employees && shown.length === 0 && (
                  <p className="text-center text-muted">No employees match.</p>
                )}
              </div>

              {employees && (
                <>
                  {shown.length > 0 && (
                  <div className="table-responsive" style={{ maxHeight: 420, overflowY: "auto" }}>
                    <table className="table table-striped table-bordered table-hover">
                      <thead>
                        <tr>
                          <th style={{ width: 36 }}>
                            <input
                              type="checkbox"
                              aria-label="Select all"
                              checked={Boolean(allSelected)}
                              onChange={toggleAllEmployees}
                            />
                          </th>
                          <th>Employee</th>
                          <th>Designation</th>
                          <th>Branch</th>
                          <th>Exemption</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shown.map((employee) => (
                          <tr key={employee.user_id}>
                            <td>
                              <input
                                type="checkbox"
                                aria-label={`Select ${employee.display_name}`}
                                checked={selected.has(employee.user_id)}
                                onChange={() => toggleEmployee(employee.user_id)}
                              />
                            </td>
                            <td>{employee.display_name}</td>
                            <td>{employee.designation}</td>
                            <td>{employee.branch}</td>
                            <td>
                              {employee.exemptions.length === 0
                                ? "-"
                                : employee.exemptions.map((x) => describeExemption(x)).join("; ")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  )}

                  <div className="pa-form-section pa-form-section--footer">
                    <div className="row">
                      <div className="col-sm-3 form-group">
                        <label>Start date</label>
                        <DateInput value={range.start} onChange={(value) => setRange({ ...range, start: value })} />
                      </div>
                      <div className="col-sm-3 form-group">
                        <label>End date</label>
                        <DateInput
                          value={range.end}
                          disabled={range.unlimited}
                          onChange={(value) => setRange({ ...range, end: value })}
                        />
                      </div>
                      <div className="col-sm-3 form-group pa-filter-option">
                        <label style={{ fontWeight: 400 }}>
                          <input
                            type="checkbox"
                            checked={range.unlimited}
                            onChange={(event) =>
                              setRange({ ...range, unlimited: event.target.checked, end: "" })
                            }
                          />{" "}
                          Unlimited
                        </label>
                      </div>
                      <div className="col-sm-3 form-group pa-filter-option">
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={saving}
                          onClick={askToConfirm}
                        >
                          {`Save (${selected.size} selected)`}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : null}

        <div className="ibox float-e-margins">
          <div className="ibox-title">
            <h5>Registered exemptions</h5>
            <div className="ibox-tools">
              <button
                type="button"
                className="btn btn-white btn-xs"
                disabled={loadingRegistered || activeRows.length === 0}
                onClick={exportActive}
              >
                Export active ({activeRows.length})
              </button>
            </div>
          </div>
          <div className="ibox-content">
            {loadingRegistered && <LoadingIndicator backgroundColor="#ffffff" />}
            {!loadingRegistered && registered.length === 0 && (
              <p className="text-center text-muted">No exemptions are registered.</p>
            )}
            {!loadingRegistered && registered.length > 0 && (
              <div className="table-responsive">
                <table className="table table-striped table-bordered table-hover">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Period</th>
                      <th>State</th>
                      <th>Registered</th>
                      <th style={{ width: 90 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {registered.map((exemption) => (
                      <tr key={exemption.exemption_id}>
                        <td>{exemption.display_name || exemption.user_id}</td>
                        <td>{describeExemption(exemption)}</td>
                        <td>{STATE_LABELS[exemption.state] || exemption.state}</td>
                        <td>{exemption.created_date}</td>
                        <td>
                          {permission.isEdit ? (
                            <button
                              type="button"
                              className="btn btn-danger btn-xs"
                              onClick={() => setToDelete(exemption)}
                            >
                              Delete
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {confirming && (
        <ActionDialog
          visible
          title="Confirm exemption"
          width={620}
          confirmLabel="Confirm and save"
          onClose={() => setConfirming(false)}
          onConfirm={save}
          busy={saving}
        >
          <p>
            Exempt <strong>{selectedEmployees.length}</strong> employee{selectedEmployees.length === 1 ? "" : "s"} from
            attendance tracking{" "}
            <strong>
              {range.unlimited
                ? `from ${formatDate(range.start)}, unlimited`
                : `from ${formatDate(range.start)} to ${formatDate(range.end)}`}
            </strong>
            ?
          </p>
          <div
            data-testid="exemption-confirm-list"
            style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #e7eaec", padding: "6px 10px", fontSize: 12 }}
          >
            {selectedEmployees.map((employee) => (
              <div key={employee.user_id} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                <strong>{employee.employment_id}</strong> {employee.fullname}
                <span className="text-muted"> - {employee.designation}</span>
              </div>
            ))}
          </div>
          <p className="text-muted" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
            Exempt staff get no pending absences for these dates.
          </p>
        </ActionDialog>
      )}

      <ActionDialog
        visible={toDelete !== null}
        title="Delete exemption"
        confirmLabel="Delete"
        confirmClassName="btn btn-danger"
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
        busy={deleting}
      >
        {toDelete && (
          <>
            <p>
              Delete the exemption of <strong>{toDelete.display_name}</strong> ({describeExemption(toDelete)})?
            </p>
            <div className="alert alert-warning">
              Days this exemption covered will become pending absences again if the employee has no
              attendance or leave on them.
            </div>
          </>
        )}
      </ActionDialog>
    </div>
  );
}
